import type { JSONSchema7 } from 'json-schema';
import type { NodeOptions } from '@unifyapps/network/generated/models/nodeOptions';
import type { ConditionEvaluationObject } from '../conditionEvaluationObject';
import type { Event } from '../event';
import type { InterfacePermissions } from '../interface';
import type { Entity } from '../page/entity';
import type { Query } from '../query';
import type { DependsOnType, DynamicPath } from '../page';
import type { StandardDataSourceIds } from '../dataSourceIds';

/**
 * Make sure that this type is in sync with the type defined in the API e_data_source on backend https://qa.unifyapps.com/api/entity-type?entityType=e_data_source
 */
type ContentType = 'none' | 'raw';

type InputLanguage = 'json' | 'xml' | 'text' | 'javascript' | 'html';

type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
export interface HTTPRequest {
  method: HTTPMethod; // HTTP method of the request (e.g., GET, POST, PUT, DELETE)
  url: string; // The URL of the request
  queryParams?: Record<string, string>; // Query parameters of the request
  headers?: Record<string, string>; // HTTP headers of the request
  body?: {
    contentType: ContentType; // The content type of the request body
    inputLanguage?: InputLanguage; // The language of the input text
    input?: string | Record<string, unknown>; // The text or JSON object to send in the request body
  } & (
    | {
        contentType: 'raw';
        inputLanguage: 'json';
        input: Record<string, unknown>; // The JSON object to send in the request body
      }
    | {
        contentType: 'raw';
        inputLanguage: Exclude<InputLanguage, 'json'>;
        input: string; // The text to send in the request body
      }
    | {
        contentType: Exclude<ContentType, 'raw'>;
      }
  );
}

export interface ApiEndpointInputsType {
  apiEndpointId: string;
  httpRequest: HTTPRequest;
}

export interface QueryInputsType {
  payload: Query;
}

export type AutomationInputsType = {
  automationId?: string;
  parameters?: Record<string, unknown>;
};

export type NodeContextType = {
  type?: 'APPLICATION';
  appName?: string;
  resourceName?: string;
  resourceVersion?: number;
  connectionId?: string;
};

export type ApplicationAdvancedOptionsType = {
  /**
   * Automatic or manual run behaviour
   */
  runBehaviour?: RunBehaviourEnums;
  /**
   * condition went the query will not fire, we send this to react-query after evaluation
   */
  queryDisabled?: ConditionEvaluationObject;
  /**
   * show a message in the snackbar when a query is disabled
   */
  queryDisabledMessage?: string;
  /**
   * refetch the query when the window is refocused (user comes back to tab), default is true
   */
  refetchOnWindowFocus?: boolean;
  /**
   * call the data source directly from the client side, default is false
   */
  useDirectApi?: boolean;
  timing?: {
    /**
     * execute a query as soon as GlobalStateStore is loaded
     */
    runQueryOnPageLoad?: boolean;
    /**
     * keep running the query after every interval
     */
    runQueryPeriodically?: boolean;
    runQueryPeriodInterval?: number;
  };
  /**
   * permissions for the data source
   * this is used on the backend to check if the data source is accessible,
   */
  permissions?: InterfacePermissions;
};

export type DataSourceCallbacks = {
  /** @deprecated Use successEvents instead */
  onSuccess?: Record<string, Event>;
  /** @deprecated Use failureEvents instead */
  onFailure?: Record<string, Event>;
  successEvents?: Event[];
  failureEvents?: Event[];
};

export enum RunBehaviourEnums {
  MANUAL = 'manual',
  AUTOMATIC = 'automatic',
}

type BaseDataSource = {
  interfacePageId: string;
  name?: string;
  context?: NodeContextType;
  inputs?: Record<string, unknown>;
  options?: NodeOptions;
  advancedOptions?: ApplicationAdvancedOptionsType;
  callbacks?: DataSourceCallbacks;
  dP?: DynamicPath[];
  dpOn?: DependsOnType[];
  standardDataSourceId?: StandardDataSourceIds;
  /**
   * The transformer code for the data source,
   * the transformation is applied to the data after the query is executed
   */
  transformer?: string;
  /**
   * when there is transformer configured in the code,
   * the output schema is no longer standard, so we need to save it everytime we run the data source,
   * in order to show it in builder
   */
  outputSchema?: JSONSchema7;
};

export type ApplicationDataSource = BaseDataSource & {
  type: 'APPLICATION';
  inputs?: AutomationInputsType;
  advancedOptions?: ApplicationAdvancedOptionsType;
};

type QueryByUADataSource = BaseDataSource & {
  type: 'QUERY_BY_UA';
  inputs?: QueryInputsType;
};

type ApiEndpointDataSource = BaseDataSource & {
  type: 'API_ENDPOINT';
  inputs?: ApiEndpointInputsType;
};

export type DataSource = ApplicationDataSource | QueryByUADataSource | ApiEndpointDataSource;

export type DataSourceEntity = Entity<DataSource>;
